import { Injectable, signal, computed } from '@angular/core';
import { StatusEntity } from '../../domain/entities/status.entity';
import { StatusDetailEntity } from '../../domain/entities/status-detail.entity';
import { StatusCategoryEntity } from '../../domain/entities/status-category.entity';

@Injectable({ providedIn: 'root' })
export class StatusState {
  private readonly _casesStatuses = signal<StatusEntity[]>([]);
  private readonly _investigationsStatuses = signal<StatusEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _casesStatusesCount = signal<number>(0);
  private readonly _investigationsStatusesCount = signal<number>(0);

  private readonly _categories = signal<StatusCategoryEntity[]>([]);
  private readonly _categoriesLoading = signal(false);
  private readonly _categoriesError = signal<string | null>(null);

  private readonly _selectedStatus = signal<StatusDetailEntity | null>(null);
  private readonly _detailLoading = signal(false);

  private readonly _saving = signal(false);
  private readonly _errorSavingStatus = signal<string | null>(null);
  private readonly _savingStatusActive = signal(false);
  private readonly _isDeletingStatus = signal(false);

  public readonly casesStatuses = this._casesStatuses.asReadonly();
  public readonly investigationsStatuses = this._investigationsStatuses.asReadonly();
  public readonly loading = this._loading.asReadonly();
  public readonly error = this._error.asReadonly();
  public readonly casesStatusesCount = this._casesStatusesCount.asReadonly();
  public readonly investigationsStatusesCount = this._investigationsStatusesCount.asReadonly();
  public readonly hasCasesStatuses = computed(() => this._casesStatuses().length > 0);
  public readonly hasInvestigationsStatuses = computed(() => this._investigationsStatuses().length > 0);

  public readonly categories = this._categories.asReadonly();
  public readonly categoriesLoading = this._categoriesLoading.asReadonly();
  public readonly categoriesError = this._categoriesError.asReadonly();

  public readonly selectedStatus = this._selectedStatus.asReadonly();
  public readonly detailLoading = this._detailLoading.asReadonly();

  public readonly saving = this._saving.asReadonly();
  public readonly errorSavingStatus = this._errorSavingStatus.asReadonly();
  public readonly savingStatusActive = this._savingStatusActive.asReadonly();
  public readonly isDeletingStatus = this._isDeletingStatus.asReadonly();

  public setCasesStatuses(statuses: StatusEntity[]): void {
    this._casesStatuses.set(statuses);
  }

  public setInvestigationsStatuses(statuses: StatusEntity[]): void {
    this._investigationsStatuses.set(statuses);
  }

  public setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  public setError(error: string | null): void {
    this._error.set(error);
    this._loading.set(false);
  }

  public clearError(): void {
    this._error.set(null);
  }

  public setCategories(categories: StatusCategoryEntity[]): void {
    this._categories.set(categories);
  }

  public setCategoriesLoading(loading: boolean): void {
    this._categoriesLoading.set(loading);
  }

  public setCategoriesError(error: string | null): void {
    this._categoriesError.set(error);
    this._categoriesLoading.set(false);
  }

  public clearCategoriesError(): void {
    this._categoriesError.set(null);
  }

  public setSelectedStatus(status: StatusDetailEntity | null): void {
    this._selectedStatus.set(status);
  }

  public setDetailLoading(loading: boolean): void {
    this._detailLoading.set(loading);
  }

  public setSaving(saving: boolean): void {
    this._saving.set(saving);
  }

  public setErrorSavingStatus(error: string | null): void {
    this._errorSavingStatus.set(error);
    this._saving.set(false);
  }

  public clearErrorSavingStatus(): void {
    this._errorSavingStatus.set(null);
  }

  public updateStatusInCasesList(updated: StatusEntity): void {
    this._casesStatuses.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
  }

  public updateStatusInInvestigationsList(updated: StatusEntity): void {
    this._investigationsStatuses.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
  }

  public removeStatusFromCasesList(id: string): void {
    this._casesStatuses.update((list) => list.filter((s) => s.id !== id));
  }

  public removeStatusFromInvestigationsList(id: string): void {
    this._investigationsStatuses.update((list) => list.filter((s) => s.id !== id));
  }

  public refreshCasesStatuses(): void {
    // Force signal update to trigger reactivity
    this._casesStatuses.update((list) => [...list]);
  }

  public refreshInvestigationsStatuses(): void {
    // Force signal update to trigger reactivity
    this._investigationsStatuses.update((list) => [...list]);
  }

  public setSavingStatusActive(saving: boolean): void {
    this._savingStatusActive.set(saving);
  }

  public setDeletingStatus(deleting: boolean): void {
    this._isDeletingStatus.set(deleting);
  }

  public setCasesStatusesCount(count: number): void {
    this._casesStatusesCount.set(count);
  }

  public setInvestigationsStatusesCount(count: number): void {
    this._investigationsStatusesCount.set(count);
  }

  public clear(): void {
    this._casesStatuses.set([]);
    this._investigationsStatuses.set([]);
    this._casesStatusesCount.set(0);
    this._investigationsStatusesCount.set(0);
    this._categories.set([]);
    this._categoriesLoading.set(false);
    this._categoriesError.set(null);
    this._selectedStatus.set(null);
    this._loading.set(false);
    this._error.set(null);
    this._saving.set(false);
    this._errorSavingStatus.set(null);
    this._detailLoading.set(false);
    this._savingStatusActive.set(false);
    this._isDeletingStatus.set(false);
  }
}
